import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const useAuthRefresh = () => {
  const { toast } = useToast();

  useEffect(() => {
    // Skip auth refresh during payment and booking flows to prevent logout
    const isPaymentFlow = () => {
      const path = window.location.pathname;
      const search = window.location.search;
      return path.includes('/payment') || 
             path.includes('/booking-success') ||
             search.includes('session_id') ||
             search.includes('booking_id') ||
             search.includes('payment_intent');
    };

    // Check and refresh session every 5 minutes
    const refreshInterval = setInterval(async () => {
      // Skip refresh during payment flows
      if (isPaymentFlow()) {
        console.log('Skipping auth refresh during payment flow');
        return;
      }

      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session check error:', error);
          return;
        }

        if (session) {
          // Check if token is about to expire (within 5 minutes)
          const expiresAt = new Date(session.expires_at! * 1000);
          const now = new Date();
          const timeUntilExpiry = expiresAt.getTime() - now.getTime();
          const fiveMinutesInMs = 5 * 60 * 1000;

          if (timeUntilExpiry < fiveMinutesInMs) {
            console.log('Refreshing session token...');
            const { error: refreshError } = await supabase.auth.refreshSession();
            
            if (refreshError) {
              console.error('Token refresh failed:', refreshError);
              
              // Only force logout for critical errors and not during payment flows
              const criticalErrors = ['invalid_token', 'token_expired', 'invalid_refresh_token'];
              const shouldForceLogout = !isPaymentFlow() && criticalErrors.some(error => 
                refreshError.message?.toLowerCase().includes(error)
              );
              
              if (shouldForceLogout) {
                console.log('Critical refresh error, forcing logout');
                // Import supabase dynamically to avoid circular dependency
                import('@/integrations/supabase/client').then(({ supabase }) => {
                  supabase.auth.signOut();
                });
                
                toast({
                  variant: "destructive",
                  title: "会话已过期",
                  description: "正在重新登录...",
                  duration: 8000,
                });
              } else if (!isPaymentFlow()) {
                toast({
                  variant: "destructive",
                  title: "会话刷新失败",
                  description: "请重新登录以继续使用",
                  duration: 8000,
                });
              }
            } else {
              console.log('Session refreshed successfully');
            }
          }
        }
      } catch (error) {
        console.error('Error checking session:', error);
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    // Clean up interval on unmount
    return () => clearInterval(refreshInterval);
  }, [toast]);

  // Function to manually refresh session
  const refreshSession = async () => {
    try {
      const { error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('Manual session refresh failed:', error);
        toast({
          variant: "destructive",
          title: "Refresh Failed",
          description: "Unable to refresh session, please log in again",
        });
        return false;
      }
      console.log('Session manually refreshed');
      return true;
    } catch (error) {
      console.error('Error manually refreshing session:', error);
      return false;
    }
  };

  return { refreshSession };
};